import React, { useState, useEffect, useRef } from "react";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import SignatureModal from "../components/common/SignatureModal";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getContacts } from "../utils/contactUtils";
import { getActions } from "../utils/actionUtils";
import { getJobs } from "../utils/jobUtils";
import {
  saveLog,
  startTimer,
  endTimer,
  getTimer,
  editLog,
} from "../utils/logUtils";
import {
  InformationCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    marginBottom: 12,
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  infoIcon: {
    padding: 12,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  signatureContainer: {
    marginBottom: 12,
  },
  signatureButton: {
    marginBottom: 12,
    borderRadius: 12,
  },
  signaturePreview: {
    width: "100%",
    height: 100,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 12,
    objectFit: "contain",
  },
  timeToggle: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
  },
  toggleButton: {
    flex: 1,
    padding: 12,
    textAlign: "center",
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& span": {
        color: COLORS.white,
      },
    },
  },
  activeToggle: {
    backgroundColor: COLORS.tertiary,
    border: "1px solid #000000",
    color: COLORS.text,
  },
  toggleText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
  },
  timerContainer: {
    marginBottom: 12,
  },
  timerButton: {
    marginBottom: 12,
    borderRadius: 12,
  },
  manualTimeContainer: {
    marginBottom: 12,
  },
  datePickerContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 56,
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
    },
  },
  datePicker: {
    flex: 1,
    height: 56,
    padding: 12,
    border: "none",
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: "transparent",
    outline: "none",
  },
  datePickerText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    padding: "0 12px",
    width: 120,
  },
  saveButton: {
    marginTop: 12,
    marginBottom: 32,
    borderRadius: 12,
  },
  infoText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    marginBottom: 12,
  },
  totalPrice: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    marginBottom: 12,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    select: {
      height: 48,
    },
    datePickerContainer: {
      height: 48,
    },
    datePicker: {
      padding: 10,
      fontSize: 14,
    },
    datePickerText: {
      fontSize: 14,
      padding: "0 10px",
    },
    toggleButton: {
      padding: 10,
    },
    toggleText: {
      fontSize: 14,
    },
  },
};

const DriverLogScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [actions, setActions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [selectedActionId, setSelectedActionId] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isManualTime, setIsManualTime] = useState(false);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoContent, setInfoContent] = useState("");
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [editingLogId, setEditingLogId] = useState(null);
  const signatureRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactList, actionList, jobList] = await Promise.all([
          getContacts(),
          getActions(),
          getJobs(),
        ]);
        const isDriver = userType === "driver" || userType === "both";
        const isEquipmentMan =
          userType === "equipmentMan" || userType === "both";
        setContacts(
          contactList
            .map((contact) => ({
              id: contact.id,
              name: contact.name || "Unnamed Contact",
              description: contact.description || "",
            }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        setActions(
          actionList
            .filter(
              (action) =>
                (isDriver && action.roles.isDriver) ||
                (isEquipmentMan && action.roles.isEquipmentMan)
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        setJobs(
          jobList
            .filter(
              (job) =>
                (isDriver && job.roles.isDriver) ||
                (isEquipmentMan && job.roles.isEquipmentMan)
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
        const timerData = await getTimer(user.uid);
        if (timerData) {
          setIsTimerRunning(true);
          setStartTime(new Date(timerData.startTime));
        }
      } catch (error) {
        alert("Failed to fetch data");
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [user.uid, userType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const log = params.get("log")
      ? JSON.parse(decodeURIComponent(params.get("log")))
      : null;
    if (log) {
      console.log("Log data received:", log);
      console.log("Available jobs:", jobs);

      setEditingLogId(log.id);
      setSelectedContactId(
        contacts.find((c) => c.name === log.contactName)?.id || ""
      );
      setSelectedActionId(
        actions.find((a) => a.name === log.actionName)?.id || ""
      );

      // Normalize jobName and jobNumber from log
      let logJobName = log.jobName || "";
      let logJobNumber = "";
      if (log.jobName && log.jobName.includes(" - ")) {
        [logJobNumber, logJobName] = log.jobName.split(" - ");
        logJobName = logJobName.trim();
        logJobNumber = logJobNumber.trim();
      }

      // Find matching job
      let selectedJob = jobs.find(
        (j) =>
          (j.jobNumber &&
            j.jobNumber === logJobNumber &&
            j.name === logJobName) ||
          j.name === log.jobName ||
          `${j.jobNumber} - ${j.name}` === log.jobName
      );

      if (!selectedJob) {
        console.warn(
          `No job found for jobName: "${log.jobName}", jobNumber: "${logJobNumber}", normalized jobName: "${logJobName}"`
        );
      } else {
        console.log("Selected job:", selectedJob);
      }

      setSelectedJobId(selectedJob?.id || "");
      setSelectedUnit(log.unit || "");
      setQuantity(log.quantity?.toString() || "");
      setNotes(log.notes || "");
      setSignature(log.signature || "");
      setStartTime(log.startTime ? new Date(log.startTime) : null);
      setEndTime(log.endTime ? new Date(log.endTime) : null);
      setIsManualTime(!!log.startTime && !!log.endTime);
    }
  }, [location.search, contacts, actions, jobs]);

  const handleStartTimer = async () => {
    const now = new Date();
    setStartTime(now);
    setIsTimerRunning(true);
    try {
      await startTimer(user.uid, now);
    } catch (error) {
      alert("Failed to start timer");
    }
  };

  const handleEndTimer = async () => {
    const now = new Date();
    setEndTime(now);
    setIsTimerRunning(false);
    try {
      await endTimer(user.uid);
    } catch (error) {
      alert("Failed to end timer");
    }
  };

  const handleShowInfo = (content) => {
    setInfoContent(content);
    setInfoModalVisible(true);
  };

  const handleSignature = (signatureData) => {
    if (signatureData) {
      setSignature(signatureData);
    }
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignature("");
    }
  };

  const handleSaveSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      handleSignature(signatureData);
      setSignatureModalVisible(false);
    } else {
      alert("Signature canvas not ready");
    }
  };

  const openSignatureModal = () => {
    setSignature("");
    handleClearSignature();
    setSignatureModalVisible(true);
  };

  const calculateTotalPrice = () => {
    if (!selectedActionId || !selectedUnit) return 0;
    const action = actions.find((a) => a.id === selectedActionId);
    if (!action || !action.prices[selectedUnit]) return 0;
    const adminPrice = action.prices[selectedUnit];

    if (selectedUnit === "bag" || selectedUnit === "gallon") {
      const qty = parseFloat(quantity) || 0;
      return adminPrice * qty;
    } else if (selectedUnit === "trip") {
      return adminPrice;
    } else if (selectedUnit === "hour" || selectedUnit === "day") {
      if (!startTime || !endTime) return 0;
      const durationMs = endTime - startTime;
      const duration =
        selectedUnit === "hour"
          ? durationMs / (1000 * 60 * 60)
          : durationMs / (1000 * 60 * 60 * 24);
      return adminPrice * duration;
    }
    return 0;
  };

  const handleSaveLog = async () => {
    if (
      !selectedContactId ||
      !selectedActionId ||
      !selectedJobId ||
      !selectedUnit ||
      !notes ||
      !signature ||
      (selectedUnit === "bag" && !quantity) ||
      (selectedUnit === "gallon" && !quantity) ||
      ((selectedUnit === "hour" || selectedUnit === "day") &&
        (!startTime || (!endTime && !isTimerRunning)))
    ) {
      alert(
        "Please fill in all required fields, times, and provide a signature"
      );
      return;
    }
    setLoading(true);
    try {
      const selectedContact = contacts.find((c) => c.id === selectedContactId);
      const selectedAction = actions.find((a) => a.id === selectedActionId);
      const selectedJob = jobs.find((j) => j.id === selectedJobId);
      const totalPrice = calculateTotalPrice();
      const logData = {
        driverId: user.uid,
        contactName: selectedContact.name,
        actionName: selectedAction.name,
        jobName: selectedJob
          ? `${selectedJob.jobNumber} - ${selectedJob.name}`
          : "Unknown Job",
        jobNumber: selectedJob ? selectedJob.jobNumber : "Unknown Job Number",
        unit: selectedUnit,
        quantity:
          selectedUnit === "bag" || selectedUnit === "gallon"
            ? parseFloat(quantity)
            : null,
        notes,
        totalPrice,
        signature,
        startTime: startTime ? startTime.toISOString() : null,
        endTime: endTime ? endTime.toISOString() : null,
        duration:
          startTime && endTime
            ? selectedUnit === "hour"
              ? (endTime - startTime) / (1000 * 60 * 60)
              : (endTime - startTime) / (1000 * 60 * 60 * 24)
            : 0,
        createdAt: new Date().toISOString(),
      };
      if (editingLogId) {
        await editLog(editingLogId, logData);
        alert("Log updated successfully");
      } else {
        await saveLog(logData);
        alert("Log saved successfully");
      }
      setEditingLogId(null);
      setSelectedContactId("");
      setSelectedActionId("");
      setSelectedJobId("");
      setSelectedUnit("");
      setQuantity("");
      setNotes("");
      setSignature("");
      setStartTime(null);
      setEndTime(null);
      setIsManualTime(false);
      setIsTimerRunning(false);
      handleClearSignature();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderPicker = (label, value, onChange, items, type) => (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.select}
      >
        <option value="">Select {label}</option>
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.jobNumber ? `${item.jobNumber} - ${item.name}` : item.name}
          </option>
        ))}
      </select>
    </div>
  );

  const renderUnitPicker = () => {
    if (!selectedActionId) return null;
    const action = actions.find((a) => a.id === selectedActionId);
    if (!action || !action.prices) return null;
    const availableUnits = Object.keys(action.prices).filter(
      (unit) => action.prices[unit] !== "" && action.prices[unit] !== undefined
    );
    if (availableUnits.length === 0) return null;

    return (
      <select
        value={selectedUnit}
        onChange={(e) => {
          setSelectedUnit(e.target.value);
          setQuantity("");
        }}
        style={styles.select}
      >
        <option value="">Select Unit</option>
        {availableUnits.map((unit) => (
          <option key={unit} value={unit}>
            {unit.charAt(0).toUpperCase() + unit.slice(1)}
          </option>
        ))}
      </select>
    );
  };

  const CustomDateInput = ({ value, onClick, placeholder }) => (
    <div style={styles.datePickerContainer}>
      <input
        type="text"
        value={value}
        onClick={onClick}
        readOnly
        style={styles.datePicker}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>
            {editingLogId ? "Edit Log" : "Driver Log"}
          </h1>
        </div>
        {fetching ? (
          <div style={styles.loader}>Loading...</div>
        ) : (
          <>
            <span style={styles.sectionTitle}>Contact</span>
            {renderPicker(
              "Contact",
              selectedContactId,
              setSelectedContactId,
              contacts,
              "contact"
            )}
            <span style={styles.sectionTitle}>Action</span>
            {renderPicker(
              "Action",
              selectedActionId,
              setSelectedActionId,
              actions,
              "action"
            )}
            <span style={styles.sectionTitle}>Unit</span>
            {renderUnitPicker()}
            {(selectedUnit === "bag" || selectedUnit === "gallon") && (
              <CustomInput
                value={quantity}
                onChangeText={setQuantity}
                icon={
                  <DocumentTextIcon
                    width={24}
                    height={24}
                    stroke={COLORS.primary}
                  />
                }
                placeholder="Enter quantity"
                type="number"
                style={{ marginBottom: 12 }}
              />
            )}
            {selectedUnit && (
              <span style={styles.totalPrice}>
                Total Price: ${calculateTotalPrice().toFixed(2)}
              </span>
            )}
            <p></p>
            <span style={styles.sectionTitle}>Job</span>
            {renderPicker("Job", selectedJobId, setSelectedJobId, jobs, "job")}
            <span style={styles.sectionTitle}>Notes</span>
            <CustomInput
              value={notes}
              onChangeText={setNotes}
              icon={
                <DocumentTextIcon
                  width={24}
                  height={24}
                  stroke={COLORS.primary}
                />
              }
              placeholder="Enter notes"
              multiline
              style={{ marginBottom: 12 }}
            />
            <span style={styles.sectionTitle}>Signature</span>
            <div style={styles.signatureContainer}>
              <CustomButton
                title={signature ? "Sign Again" : "Add Signature"}
                onPress={openSignatureModal}
                style={styles.signatureButton}
              />
              {signature && (
                <img
                  src={signature}
                  alt="Signature"
                  style={styles.signaturePreview}
                />
              )}
            </div>
            <span style={styles.sectionTitle}>Time</span>
            <div style={styles.timeToggle}>
              <button
                style={{
                  ...styles.toggleButton,
                  ...(isManualTime ? {} : styles.activeToggle),
                }}
                onClick={() => setIsManualTime(false)}
              >
                <span style={styles.toggleText}>Timer</span>
              </button>
              <button
                style={{
                  ...styles.toggleButton,
                  ...(isManualTime ? styles.activeToggle : {}),
                }}
                onClick={() => setIsManualTime(true)}
              >
                <span style={styles.toggleText}>Manual</span>
              </button>
            </div>
            {!isManualTime ? (
              <div style={styles.timerContainer}>
                <CustomButton
                  title={isTimerRunning ? "End Timer" : "Start Timer"}
                  onPress={isTimerRunning ? handleEndTimer : handleStartTimer}
                  style={styles.timerButton}
                  disabled={loading}
                />
                {startTime && (
                  <span style={{ ...styles.infoText, display: "block" }}>
                    Started: {startTime.toLocaleString()}
                  </span>
                )}
                {endTime && (
                  <span style={{ ...styles.infoText, display: "block" }}>
                    Ended: {endTime.toLocaleString()}
                  </span>
                )}
              </div>
            ) : (
              <div style={styles.manualTimeContainer}>
                <div style={styles.datePickerContainer}>
                  <span style={styles.datePickerText}>Start Time</span>
                  <DatePicker
                    selected={startTime}
                    onChange={(date) => setStartTime(date)}
                    showTimeSelect
                    dateFormat="MM/dd/yyyy h:mm aa"
                    placeholderText="Start date and time"
                    customInput={
                      <CustomDateInput placeholder="Start date and time" />
                    }
                    withPortal={false}
                  />
                </div>
                <div style={styles.datePickerContainer}>
                  <span style={styles.datePickerText}>End Time</span>
                  <DatePicker
                    selected={endTime}
                    onChange={(date) => setEndTime(date)}
                    showTimeSelect
                    dateFormat="MM/dd/yyyy h:mm aa"
                    placeholderText="End date and time"
                    customInput={
                      <CustomDateInput placeholder="End date and time" />
                    }
                    withPortal={false}
                  />
                </div>
              </div>
            )}
            <CustomButton
              title={
                loading ? "Saving..." : editingLogId ? "Update Log" : "Save Log"
              }
              onPress={handleSaveLog}
              style={styles.saveButton}
              disabled={loading}
            />
          </>
        )}
        <CustomModal
          isVisible={infoModalVisible}
          onClose={() => setInfoModalVisible(false)}
          title="Description"
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.tertiary}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <span style={styles.infoText}>{infoContent}</span>
        </CustomModal>
        <SignatureModal
          isVisible={signatureModalVisible}
          onClose={() => setSignatureModalVisible(false)}
          onSave={handleSaveSignature}
          onClear={handleClearSignature}
          signatureRef={signatureRef}
          onSignature={handleSignature}
        />
      </div>
    </div>
  );
};

export default DriverLogScreen;
